import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATED, ROUTER_NAVIGATION } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { SalesYearsService } from '../../shared/services/sales-years.service';
import { SnackbarService } from '../../shared/services/snackbar.service';
import * as globalActions from '../actions';
import { selectSalesYearFromRoute, selectSalesYearsChanging, selectSelectedSalesYear } from '../selectors';

@Injectable()
export class SalesYearsEffects {
  private actions$ = inject(Actions);
  private globalService = inject(SalesYearsService);
  private snackbarService = inject(SnackbarService);
  private store = inject(Store);

  loadSalesYears$ = createEffect(() =>
    this.actions$.pipe(
      ofType(globalActions.loadSalesYears),
      switchMap(() => {
        return this.globalService.fetchSalesYears().pipe(
          map((salesYears) => globalActions.loadSalesYearsSuccess({ salesYears })),
          catchError((error) => {
            this.snackbarService.error('Error Fetching Sales Years');
            return of(globalActions.loadSalesYearsFail(error));
          })
        );
      })
    )
  );

  initializeSelectedSalesYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(globalActions.loadSalesYearsSuccess),
      concatLatestFrom(() => this.store.select(selectSalesYearFromRoute)),
      filter(([, salesYearFromRoute]) => salesYearFromRoute !== undefined),
      map(([, salesYearFromRoute]) => {
        return globalActions.changeSalesYear({ newSalesYear: salesYearFromRoute! });
      })
    )
  );

  handleNavigation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      concatLatestFrom(() => [this.store.select(selectSalesYearFromRoute), this.store.select(selectSelectedSalesYear)]),
      filter(
        ([, salesYearFromRoute, selectedSalesYear]) =>
          salesYearFromRoute !== undefined && salesYearFromRoute !== selectedSalesYear
      ),
      map(([, salesYearFromRoute]) => {
        return globalActions.changeSalesYear({ newSalesYear: salesYearFromRoute! });
      })
    )
  );

  handleNavigationEnd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      concatLatestFrom(() => [this.store.select(selectSalesYearsChanging)]),
      filter(([, salesYearChanging]) => salesYearChanging),
      map(() => {
        return globalActions.changeSalesYearSuccess();
      })
    )
  );
}
